/**
 * Survey Welcome Screen Component
 *
 * Displays a welcome screen at the beginning of every survey with:
 * 1. Customer Facing Title
 * 2. Description
 * 3. Instructions
 * 4. Start button
 */

import React from 'react';
import { Play, MessageCircle, Mic } from 'lucide-react';

interface WelcomeScreenData {
  title: string;
  description: string;
  instructions: string;
}

interface SurveyWelcomeScreenProps {
  welcomeData: WelcomeScreenData;
  onStart: () => void;
  className?: string;
}

const SurveyWelcomeScreen: React.FC<SurveyWelcomeScreenProps> = ({
  welcomeData,
  onStart,
  className = '',
}) => {
  const { title, description, instructions } = welcomeData;

  return (
    <div className={`min-h-screen bg-gray-50 py-8 ${className}`}>
      <div className="max-w-4xl mx-auto px-4">
        <div className="bg-white rounded-xl shadow-sm">
          {/* Header with Icon */}
          <div className="px-6 pt-6 pb-4 border-b border-gray-200">
            <div className="flex items-center justify-center mb-6">
              <div className="bg-slate-100 rounded-full p-4">
                <MessageCircle className="w-10 h-10 text-slate-600" />
              </div>
            </div>
            <h1 className="text-4xl font-bold text-slate-900 text-center">{title}</h1>
          </div>

          {/* Content */}
          <div className="px-6 py-8">
            {/* Description */}
            <div className="text-center mb-8">
              <p className="text-xl text-slate-700 leading-relaxed">{description}</p>
            </div>

            {/* Instructions Card */}
            {instructions && (
              <div className="bg-slate-50 rounded-xl border border-slate-200 p-6 mb-8">
                <div className="flex items-start space-x-3">
                  <div className="bg-slate-100 rounded-full p-2 mt-1">
                    <Mic className="w-4 h-4 text-slate-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900 mb-2">Before we begin:</h3>
                    <p className="text-slate-700 leading-relaxed">{instructions}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Start Button */}
            <div className="text-center">
              <button
                onClick={onStart}
                className="inline-flex items-center justify-center gap-2 text-lg px-12 py-4 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200"
              >
                <Play className="w-5 h-5" />
                Start Survey
              </button>
            </div>

            {/* Footer */}
            <div className="text-center mt-8 pt-4 border-t border-gray-100">
              <p className="text-sm text-gray-500">We're excited to hear what you have to say.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SurveyWelcomeScreen;
